import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, styled } from '@mui/material';
import { bakery2bTabState } from '../bakery2b.state.ts';
import { ControlButtons } from './controlButtons.component.tsx';
import { Table } from '../../../../../../shared/components/table/table.component.tsx';
import { storageHelper } from '../../../../../../shared/helpers/storage/index.ts';
import { ConnectBakery2bClientWithCustomer } from '../popups/connectBakery2bClientWithCustomer/connectBakery2bClientWithCustomer.popup.tsx';
import { Column } from '../../../../../../shared/components/table/components/common.components.tsx';
import { ITabState } from '../../../../states/tabState.model.ts';
import { isEnterKey } from '../../../../../../shared/helpers/utils/utils.helper.ts';
import { ClientDetailsPopup } from '../popups/clientDetails/clientDetails.popup.tsx';
import { Stack } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';

export const Bakery2bTable: FC<IBakery2bTable> = ({
  bakery2bData,
  bakery2bAccountsOptions,
  customerId,
}) => {
  const { t } = useTranslation();
  const [tableData, setTableData] = useState(bakery2bData);
  const [accountsOptions, setAccountsOptions] = useState(bakery2bAccountsOptions ?? []);
  const [selectedPos, setSelectedPos] = useState<ITabState['bakery2bAccounts'][number] | null>(
    null,
  );
  const clientDetailsPopup = useMemo(() => new ClientDetailsPopup(), []);
  const connectBakery2bClientWithCustomerPopup = useMemo(
    () => new ConnectBakery2bClientWithCustomer(),
    [],
  );
  const [currentCustomerId, setCurrentCustomerId] = useState<string>(customerId);

  useEffect(() => {
    bakery2bTabState.pub.initBakery2bAccountsData(tableData, customerId);
    const bakery2bTabStateSub = bakery2bTabState.sub
      .state()
      .subscribe(({ bakery2bAccounts, customerId }) => {
        const filteredOptions = structuredClone(
          (bakery2bAccountsOptions ?? []).filter(
            (item) =>
              !bakery2bAccounts.find((acc: any) => {
                return acc.id == item.id;
              }),
          ),
        );
        setAccountsOptions(filteredOptions);
        setCurrentCustomerId(customerId);
        setTableData(bakery2bAccounts);
        setSelectedPos(null);
      });
    return () => {
      bakery2bTabStateSub.unsubscribe();
    };
  }, []);

  return (
    <>
      <Box>
        <ControlButtons
          {...{
            selectedPos,
            currentCustomerId,
            bakery2bData: tableData,
            connectBakery2bClientWithCustomerPopup,
            bakery2bAccountsOptions: accountsOptions,
          }}
        />
        <STable
          data={tableData}
          initialState={{
            columns: storageHelper.local.getItem('customer.bakery2bColumnModel'),
          }}
          onChangeColumnModel={(v) =>
            storageHelper.local.setItem('customer.bakery2bColumnModel', v)
          }
          onRowClick={({ row }, e) => {
            clientDetailsPopup.stream.emit('open', {
              values: row,
              field: (e.target as any).parentElement?.dataset?.field,
            });
            setSelectedPos(row);
          }}
          onCellKeyDown={({ row, field }, event) => {
            if (isEnterKey(event)) {
              clientDetailsPopup.stream.emit('open', { values: row, field });
            }
          }}
          height={400}
        >
          <Column field='email' headerName={t('common.email')} sortable={false} width={380} />
          <Column field='name' headerName={t('common.name')} sortable={false} width={380} />
          <Column
            field='isDeactivated'
            headerName={t('customer.deactivated')}
            sortable={false}
            align='center'
            headerAlign='center'
            renderCell={({ value }) => (
              <Stack
                data-field='isDeactivated'
                sx={{
                  width: '100%',
                  alignItems: 'center',
                }}
              >
                <CheckIcon opacity={value ? '1' : '0'} />
              </Stack>
            )}
            width={110}
          />
        </STable>
      </Box>
      {<connectBakery2bClientWithCustomerPopup.Component />}
      {<clientDetailsPopup.Component />}
    </>
  );
};

const STable = styled(Table)({
  '& .MuiDataGrid-cell': {
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 10,
    paddingRight: 10,
    justifyContent: 'inherit',
  },
});

interface IBakery2bTable {
  bakery2bData: ITabState['bakery2bAccounts'];
  bakery2bAccountsOptions: ITabState['bakery2bTabOptions']['bakery2bAccounts'];
  customerId: string;
}
